import React from "react";
import {observer} from "mobx-react";
import {MENU_ITEM_TYPE, MenuItem, MENU_ITEM_LABEL_KEY} from "../../menu-items";
import {
    BackAsAdminMenuLink, CoverageMenuItemLink,
    DefaultMenuItem,
    DefaultMenuItemLink,
    MainMenuItemLink, MenuItemProps,
    MenuLinkProps, TopLevelCollapsibleMenuItem
} from "../menu-links/menu-items";
import { IconProps } from "../../../../../icons/icon-props-interface";
import IconSidebarSettings from "../../icon-sidebar-settings";
import IconHome from "../../icon-home";
import IconReturnGO from "../../icon-returngo";
import Icon from "../../../../../icons/icon/icon";
import appLayoutStore from "../../../../../../stores/app-layout-store";
import rmsMixpanelStore from "../../../../../../stores/rms-mixpanel-store";
import IconTroubleshooting from "../../icon-troubleshooting";
import IconAnalytics from "../../icon-analytics";
import { useNavigate } from "react-router-dom";
import pageUrlCoordinationStore from "../../../page-url-broadcaster/page-url-coordination-store";
import IconAccountHealth from "../../icon-account-health";
import { reloadManageReturnsParent } from "../../../../../../pages/manage-returns/utils/manage-returns-parent-navigation";
import { APP_PATHS } from "../../../../../../constants/app-paths";

type MenuItemContentsProps = Pick<MenuItem, 'to' | 'type'| 'label_key' | 'mixpanelEventName'> & {
    label: string;
};

const MenuItemContents: React.FC<MenuItemContentsProps> = observer(({to, type, label, label_key, mixpanelEventName}) => {
    /** TODO: uncomment this when new badge will be implemented */
    // const { getTranslation } = useDashboardTranslation();
    // const TRANSLATION_SCOPE = 'dashboard-side-menu';
    // const newBadgeText = getTranslation('NEW_BADGE_TEXT',TRANSLATION_SCOPE);

    const navigate = useNavigate();
    
    const getLinkComponent = (type?: MENU_ITEM_TYPE): React.FC<MenuLinkProps> => {
        switch (type) {
            case MENU_ITEM_TYPE.BACK_AS_ADMIN:
                return BackAsAdminMenuLink;
            case MENU_ITEM_TYPE.MAIN_MENU_ITEM:
                return MainMenuItemLink;
            case MENU_ITEM_TYPE.COVERAGE_MENU_ITEM:
                return CoverageMenuItemLink;
            default:
                return DefaultMenuItemLink;
        }
    }

    const getIconComponent = (labelKey: string | undefined): React.FC<IconProps> | undefined => {
        switch (labelKey) {
            case MENU_ITEM_LABEL_KEY.HOME:
                return IconHome;
            case MENU_ITEM_LABEL_KEY.SETTINGS:
                return IconSidebarSettings;
            case MENU_ITEM_LABEL_KEY.RETURNS_AND_EXCHANGES:
                return IconReturnGO;
            case MENU_ITEM_LABEL_KEY.TROUBLESHOOTING:
                return IconTroubleshooting;
            case MENU_ITEM_LABEL_KEY.HEALTH_PAGE:
                return IconAccountHealth;
            case MENU_ITEM_LABEL_KEY.ANALYTICS:
                return IconAnalytics;
            default:
                return undefined;
        }
    }

    const getTextComponent = (type?: MENU_ITEM_TYPE): React.FC<MenuItemProps> => {
        switch (type) {
            case MENU_ITEM_TYPE.TOP_LEVEL_COLLAPSIBLE:
                return TopLevelCollapsibleMenuItem;
            default:
                return DefaultMenuItem;
        }
    }

    // const isNew = to === SPA_APP_PATH_PATTERN.SHIP_BY_RETURNGO; // TODO: implement logic to check if the item is new
    const iconComponent = getIconComponent(label_key);

    if (to) {
        const LinkComponent = getLinkComponent(type);

        return (
            <LinkComponent key={to} label={label} to={pageUrlCoordinationStore.constructLaravelAbsoluteUrl(to)} onClick={(e) => {
                if (mixpanelEventName) {
                    rmsMixpanelStore.trackEvent(mixpanelEventName);
                }
                appLayoutStore.isSidebarOpen = false;

                /** 
                 * Define if the link in not going to be opened in the same tab and do nothing - 
                 * let a natural browser behaviour happen.
                 * The key markers are: not any additional key was pressed, left mouse button was
                 * clicked.
                 */
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
                    return;
                }

                e.preventDefault();

                /** Manage Returns needs the parent URL (and its default filters) - see reloadManageReturnsParent. */
                if (
                    label_key === MENU_ITEM_LABEL_KEY.MANAGE_RETURNS
                    || to === APP_PATHS.RETURNS
                    || to === APP_PATHS.RETURNS_NEW
                ) {
                    reloadManageReturnsParent();
                    return;
                }

                /** Navigate using react router */
                navigate(to);
            }}>
                {iconComponent && <Icon Component={iconComponent} />}
                {/* 
                29/09/2025: we hide new badge for now, until product will decide on its functionality
                {isNew &&
                    <div className="menu-badge-container">
                        <Badge type={BadgeType.SUCCESS} text={newBadgeText} />
                    </div>
                } */}
            </LinkComponent>
        );
    }
    else {

        const TextComponent = getTextComponent(type);

        return (
        <TextComponent label={label}>
            {iconComponent && <Icon Component={iconComponent} />}
        </TextComponent>
        );
    }
});

export default MenuItemContents;
